import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CatalogProductService } from 'src/app/services/catalog-product.service';
import { UserService } from 'src/app/services/user/user.service';
import Chart from 'chart.js/auto';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
declare var alertify: any;

@Component({
  selector: 'app-catalog-products',
  templateUrl: './catalog-products.component.html',
  styleUrls: ['./catalog-products.component.css']
})
export class CatalogProductsComponent implements OnInit, OnDestroy {

  loading = false;
  loadingStats = false;

  // Enhanced stats
  enhanced: any = null;

  // Paginación visual del listado de tiendas (no afecta backend)
  // Backend devuelve el universo de tiendas; el frontend pagina de 10 en 10.
  shopsPageSize = 10;
  shopsPage = 1;

  // Period filter — default "Hoy" (el dashboard arranca mostrando el día actual)
  selectedPeriod: string = 'today';
  lastUpdate: string | null = null;   // computed_at del snapshot materializado (última actualización)
  // Cache en cliente: no re-consulta si se cargó hace < 5 min (salvo refresh manual = force)
  private _clientCache = new Map<string, { data: any; ts: number; computed_at?: string | null }>();
  private CLIENT_TTL_MS = 5 * 60 * 1000;
  periods = [
    { value: 'today', label: 'Hoy' },
    { value: 'week', label: '7 días' },
    { value: 'month', label: '30 días' },
    { value: 'quarter', label: '90 días' },
    { value: 'year', label: '1 año' },
    { value: 'all', label: 'Todo' }
  ];

  // Table
  products: any[] = [];
  pagination: any = { page: 1, limit: 50, total: 0, pages: 0 };
  searchTerm: string = '';
  sortField: string = 'stats.total_purchases';
  sortOrder: string = 'desc';

  // Filters
  selectedSectors: number[] = [];   // multi-selección de sectores
  sectors: any[] = [];

  // Filtros detallados (fecha / tienda / producto / sector / plan)
  dateFrom: string = '';
  dateTo: string = '';
  selectedShops: (number | string)[] = [];   // multi-selección de tiendas
  productCode: string = '';
  shopsFilter: { code: number | string; name: string | null; has_purchases?: boolean }[] = [];
  shopNameMap: { [key: string]: string } = {};
  selectedPlans: string[] = [];   // multi-selección de planes
  plansFilter: { _id: string; code: string | null; name: string; purchases: number }[] = [];

  // Inputs de autocomplete (mantienen el texto visible, separados del valor resuelto)
  shopInput: string = '';
  sectorInput: string = '';
  planInput: string = '';
  productSearchResults: any[] = [];
  private productSearchTimeout: any;

  // Dropdown custom (autocomplete visible) — estados de apertura
  shopDropdownOpen = false;
  sectorDropdownOpen = false;
  planDropdownOpen = false;
  productDropdownOpen = false;

  // Snapshots del texto seleccionado por selector. Cuando el input coincide
  // con este snapshot, los getters de filtrado devuelven todas las opciones
  // (en lugar de filtrar con el texto completo, que quedaba vacío). Esto
  // permite que el usuario haga clic en un input ya seleccionado y vea toda
  // la lista sin perder su selección.
  shopSelectedText: string = '';
  sectorSelectedText: string = '';
  planSelectedText: string = '';

  // Estado loading del botón Aplicar
  applyingFilters: boolean = false;

  // Active tab
  activeTab: string = 'overview';

  // Create product modal
  showCreateModal = false;
  savingProduct = false;
  newProduct: any = { code: '', name: '', brand: '', category: '', ean_upc: '', material: '', numero_material: '', se: null };

  // Edit product inline
  editingProduct: any = null;
  editForm: any = { name: '', brand: '', category: '', material: '', numero_material: '', se: null };
  savingEdit = false;

  // Export
  exporting = false;

  // Charts
  private timeSeriesChart: Chart | null = null;
  private topProductsChart: Chart | null = null;
  private sectorChart: Chart | null = null;

  sectorNames: { [key: number]: string } = {
    10: 'Común',
    11: 'Calzados',
    12: 'Marroquinería',
    13: 'Bazar',
    14: 'Vestimenta',
    15: 'Juguetes',
    16: 'Accesorios',
    17: 'Belleza',
    18: 'Ferretería',
    19: 'Electrodomésticos',
    20: 'Celulares',
    21: 'Hogar',
    22: 'Deportes',
    23: 'Bebes',
    24: 'Tecnología',
    25: 'Artículos de oficina',
    26: 'Suministros',
    27: 'Alimentos',
    28: 'Farmacia',
    29: 'Licorería y conexos',
    30: 'Cuidado personal y Hogar'
  };

  getSectorName(se: number): string {
    return this.sectorNames[se] || `Sector ${se}`;
  }

  // Search debounce
  private searchTimeout: any;

  constructor(
    private catalogService: CatalogProductService,
    private router: Router,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroyCharts();
  }

  async loadData(force = false) {
    await Promise.all([
      this.loadEnhancedStats(force),
      this.loadProducts(force),
      this.loadSectors(),
      this.loadShopsFilter(),
      this.loadPlansFilter()
    ]);
  }

  async loadPlansFilter() {
    try {
      // Solo planes tipo CREDIT (excluye seguros, etc.). Se usa /api/plans/all
      // que sí filtra por product_type, en lugar de /catalog-product/plans que
      // devuelve todos los planes mezclados.
      const resp: any = await this.userService.getplansall({
        lean: true,
        product_type: 'CREDIT'
      });
      const rows = resp?.res || [];
      this.plansFilter = rows.map((p: any) => ({
        _id: p._id,
        code: p.code || null,
        name: p.name || p.description || String(p._id),
        purchases: 0
      }));
    } catch (error) {
      console.error('Error cargando planes:', error);
    }
  }

  async loadSectors() {
    try {
      const resp = await this.catalogService.getSectors();
      this.sectors = resp.res || [];
    } catch (error) {
      console.error('Error cargando sectores:', error);
    }
  }

  async loadShopsFilter() {
    try {
      const resp = await this.catalogService.getShopsFilter();
      this.shopsFilter = resp.res || [];
      this.shopNameMap = {};
      this.shopsFilter.forEach(s => {
        if (s.code !== null && s.code !== undefined) {
          this.shopNameMap[String(s.code)] = s.name || '';
        }
      });
      this.rebuildShopRows();
    } catch (error) {
      console.error('Error cargando tiendas:', error);
    }
  }

  private currentFilters() {
    return {
      period: (!this.dateFrom && !this.dateTo && this.selectedPeriod !== 'all') ? this.selectedPeriod : undefined,
      dateFrom: this.dateFrom || undefined,
      dateTo: this.dateTo || undefined,
      shop: this.selectedShops.length ? this.selectedShops.join(',') : undefined,
      productCode: this.productCode?.trim() || undefined,
      sector: this.selectedSectors.length ? this.selectedSectors.join(',') : undefined,
      plan: this.selectedPlans.length ? this.selectedPlans.join(',') : undefined
    };
  }

  async loadEnhancedStats(force = false) {
    const key = 'stats:' + JSON.stringify(this.currentFilters());
    const c = this._clientCache.get(key);
    if (!force && c && (Date.now() - c.ts) < this.CLIENT_TTL_MS) {
      // Datos frescos en cliente (<5min) → sin spinner ni llamada
      this.enhanced = c.data;
      this.lastUpdate = c.computed_at || this.lastUpdate;
      this.rebuildShopRows();
      setTimeout(() => this.buildCharts(), 100);
      return;
    }
    this.loadingStats = true;
    try {
      const resp = await this.catalogService.getEnhancedStats(this.currentFilters());
      this.enhanced = resp.res;
      this.lastUpdate = resp.computed_at || new Date().toISOString();
      this._clientCache.set(key, { data: resp.res, ts: Date.now(), computed_at: this.lastUpdate });
      this.shopsPage = 1;
      this.rebuildShopRows();
      setTimeout(() => this.buildCharts(), 100);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    } finally {
      this.loadingStats = false;
    }
  }

  async loadProducts(force = false) {
    const f = this.currentFilters();
    const params = {
      page: this.pagination.page, limit: this.pagination.limit,
      search: this.searchTerm || undefined, se: undefined,
      sort: this.sortField, order: this.sortOrder,
      period: f.period, dateFrom: f.dateFrom, dateTo: f.dateTo,
      shop: f.shop, sector: f.sector, plan: f.plan
    };
    const key = 'list:' + JSON.stringify(params);
    const c = this._clientCache.get(key);
    if (!force && c && (Date.now() - c.ts) < this.CLIENT_TTL_MS) {
      this.products = c.data.res;
      this.pagination = c.data.pagination || this.pagination;
      return;
    }
    this.loading = true;
    try {
      const resp = await this.catalogService.getList(params);
      this.products = resp.res || [];
      this.pagination = resp.pagination || this.pagination;
      this._clientCache.set(key, { data: { res: this.products, pagination: this.pagination }, ts: Date.now() });
    } catch (error) {
      console.error('Error cargando productos:', error);
    } finally {
      this.loading = false;
    }
  }

  getShopName(code: number | string): string {
    if (code === null || code === undefined) return '';
    return this.shopNameMap[String(code)] || '';
  }

  // === Autocomplete handlers ===

  onShopInputChange(v: string) {
    this.shopInput = v;
    this.shopDropdownOpen = true;
  }

  // Toggle interno del filtro de tiendas: todas | con compras | sin compras
  shopFilterMode: 'all' | 'with' | 'without' = 'all';

  // Filtrado dinámico (insensitive) + modo con/sin compras. Muestra TODAS las tiendas.
  get filteredShops(): any[] {
    const q = (this.shopInput || '').trim().toLowerCase();
    let list: any[] = this.shopsFilter;
    if (this.shopFilterMode === 'with') list = list.filter(s => (s as any).has_purchases);
    else if (this.shopFilterMode === 'without') list = list.filter(s => !(s as any).has_purchases);
    if (q) list = list.filter(s =>
      String(s.code).toLowerCase().includes(q) || (s.name || '').toLowerCase().includes(q)
    );
    return list.slice(0, 200);
  }

  isShopSelected(code: number | string): boolean {
    return this.selectedShops.some(c => String(c) === String(code));
  }

  // Multi-select: alterna la tienda en la selección y mantiene el dropdown abierto
  toggleShop(s: { code: number | string; name: string | null }) {
    const i = this.selectedShops.findIndex(c => String(c) === String(s.code));
    if (i >= 0) this.selectedShops.splice(i, 1);
    else this.selectedShops.push(s.code);
    this.shopInput = '';
    this.shopDropdownOpen = true;
  }

  removeShop(code: number | string) {
    this.selectedShops = this.selectedShops.filter(c => String(c) !== String(code));
  }

  shopLabel(code: number | string): string {
    const n = this.shopNameMap[String(code)];
    return n ? `${code} - ${n}` : String(code);
  }

  // ===== Sector (multi-select) =====
  onSectorInputChange(v: string) {
    this.sectorInput = v;
    this.sectorDropdownOpen = true;
  }

  get filteredSectors(): { se: number; count: number; total_purchases: number }[] {
    const q = (this.sectorInput || '').trim().toLowerCase();
    if (!q) return this.sectors;
    return this.sectors.filter(s => {
      const name = this.getSectorName(s.se).toLowerCase();
      return name.includes(q) || String(s.se).includes(q);
    });
  }

  isSectorSelected(se: number): boolean { return this.selectedSectors.includes(se); }
  toggleSector(s: { se: number }) {
    const i = this.selectedSectors.indexOf(s.se);
    if (i >= 0) this.selectedSectors.splice(i, 1);
    else this.selectedSectors.push(s.se);
    this.sectorInput = '';
    this.sectorDropdownOpen = true;
  }
  removeSector(se: number) { this.selectedSectors = this.selectedSectors.filter(x => x !== se); }

  // ===== Plan (multi-select) =====
  onPlanInputChange(v: string) {
    this.planInput = v;
    this.planDropdownOpen = true;
  }

  get filteredPlans(): { _id: string; code: string | null; name: string; purchases: number }[] {
    const q = (this.planInput || '').trim().toLowerCase();
    if (!q) return this.plansFilter.slice(0, 50);
    return this.plansFilter.filter(p =>
      (p.code || '').toLowerCase().includes(q) ||
      (p.name || '').toLowerCase().includes(q)
    ).slice(0, 50);
  }

  isPlanSelected(id: string): boolean { return this.selectedPlans.includes(id); }
  togglePlan(p: { _id: string }) {
    const i = this.selectedPlans.indexOf(p._id);
    if (i >= 0) this.selectedPlans.splice(i, 1);
    else this.selectedPlans.push(p._id);
    this.planInput = '';
    this.planDropdownOpen = true;
  }
  removePlan(id: string) { this.selectedPlans = this.selectedPlans.filter(x => x !== id); }
  planLabel(id: string): string {
    const p = this.plansFilter.find(x => x._id === id);
    return p ? ((p.code ? p.code + ' - ' : '') + p.name) : id;
  }

  // Cierre con delay para permitir click en opción
  closeShopDropdown()    { setTimeout(() => this.shopDropdownOpen = false, 150); }
  closeSectorDropdown()  { setTimeout(() => this.sectorDropdownOpen = false, 150); }
  closePlanDropdown()    { setTimeout(() => this.planDropdownOpen = false, 150); }
  closeProductDropdown() { setTimeout(() => this.productDropdownOpen = false, 150); }

  // Reabrir el dropdown al hacer click sobre un input. La lista de opciones
  // se muestra completa porque el getter de filtrado detecta cuando el texto
  // del input coincide con la última selección y omite el filtrado.
  onShopInputClick()   { this.shopDropdownOpen = true; }
  onSectorInputClick() { this.sectorDropdownOpen = true; }
  onPlanInputClick()   { this.planDropdownOpen = true; }

  // ===== Listado "Por Tienda": TODAS las tiendas, aunque no tengan data =====
  // Mergea el desglose con ventas (enhanced.by_shop) con la lista completa de tiendas
  // (shopsFilter); las que no vendieron en el período aparecen en 0.
  // ⚡ Memoizado: se recalcula SOLO al cargar datos (no en cada change-detection).
  // Antes era un getter que reconstruía Map+map+sort en cada CD → congelaba el navegador.
  shopRows: any[] = [];
  rebuildShopRows() {
    const byShop = new Map<string, any>((this.enhanced?.by_shop || []).map((s: any) => [String(s.shop), s]));
    const rows: any[] = (this.shopsFilter || []).map(sh => {
      const hit = byShop.get(String(sh.code));
      return hit || { shop: sh.code, shop_name: sh.name, total_units: 0, purchases: 0, revenue_usd: 0, revenue_bs: 0, unique_products: 0 };
    });
    rows.sort((a: any, b: any) => (b.revenue_usd || 0) - (a.revenue_usd || 0));
    this.shopRows = rows;
  }
  get allShopRows(): any[] { return this.shopRows; }

  // Toggle de la pestaña Tiendas: todas | con compras | sin compras
  shopTabMode: 'all' | 'with' | 'without' = 'all';
  setShopTabMode(m: 'all' | 'with' | 'without') { this.shopTabMode = m; this.shopsPage = 1; }
  get shopRowsView(): any[] {
    if (this.shopTabMode === 'with') return this.shopRows.filter(s => (s.purchases || 0) > 0);
    if (this.shopTabMode === 'without') return this.shopRows.filter(s => !(s.purchases || 0));
    return this.shopRows;
  }

  get shopsTotalPages(): number {
    return Math.max(1, Math.ceil(this.shopRowsView.length / this.shopsPageSize));
  }

  get shopsPageStart(): number {
    if (!this.shopRowsView.length) return 0;
    return (this.shopsPage - 1) * this.shopsPageSize;
  }

  get shopsPageEnd(): number {
    return Math.min(this.shopsPage * this.shopsPageSize, this.shopRowsView.length);
  }

  get paginatedShops(): any[] {
    return this.shopRowsView.slice(this.shopsPageStart, this.shopsPageEnd);
  }

  goToShopsPage(p: number) {
    const max = this.shopsTotalPages;
    this.shopsPage = Math.min(Math.max(1, p), max);
  }

  // Genera la secuencia de números de página visibles (con elipsis "…").
  // Estrategia: muestra ventana de 5 páginas alrededor de la actual, con
  // primera y última siempre visibles. Ejemplo: 1 … 4 5 6 … 12.
  get shopsPageNumbers(): (number | '…')[] {
    const total = this.shopsTotalPages;
    const current = this.shopsPage;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages: (number | '…')[] = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    if (start > 2) pages.push('…');
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total - 1) pages.push('…');
    pages.push(total);
    return pages;
  }

  onProductCodeInput(v: string) {
    this.productCode = v;
    this.productDropdownOpen = true;
    clearTimeout(this.productSearchTimeout);
    if (!v || v.trim().length < 2) { this.productSearchResults = []; return; }
    this.productSearchTimeout = setTimeout(async () => {
      try {
        const resp = await this.catalogService.search(v.trim());
        this.productSearchResults = (resp?.res || []).slice(0, 25);
      } catch (e) {
        this.productSearchResults = [];
      }
    }, 300);
  }

  selectProduct(p: any) {
    this.productCode = p.code;
    this.productDropdownOpen = false;
  }

  async applyAdvancedFilters() {
    if (this.applyingFilters) return;
    this.applyingFilters = true;
    this.pagination.page = 1;
    try {
      await Promise.all([this.loadEnhancedStats(), this.loadProducts()]);
    } finally {
      this.applyingFilters = false;
    }
  }

  clearAdvancedFilters() {
    this.dateFrom = '';
    this.dateTo = '';
    this.selectedShops = [];
    this.shopInput = '';
    this.productCode = '';
    this.productSearchResults = [];
    this.selectedSectors = [];
    this.sectorInput = '';
    this.selectedPlans = [];
    this.planInput = '';
    this.applyAdvancedFilters();
  }

  getPlanLabel(planId: string): string {
    if (!planId) return '';
    const p = this.plansFilter.find(x => x._id === planId);
    return p ? (p.code ? `${p.code} - ${p.name}` : p.name) : '';
  }

  onPeriodChange(period: string) {
    this.selectedPeriod = period;
    if (period === 'all') {
      // "Todo": sin rango → usa el materializado 'all' (rápido; no cambia día a día).
      this.dateFrom = '';
      this.dateTo = '';
    } else {
      // Traducir el período a un RANGO de fechas concreto y cargarlo en los inputs.
      // Así "Hoy"/"7 días"/etc. == seleccionar ese rango a mano: mismo cálculo EN VIVO,
      // siempre actualizado (no depende de snapshots materializados congelados).
      const daysBack: { [k: string]: number } = { today: 0, week: 6, month: 29, quarter: 89, year: 364 };
      const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const today = new Date();
      const from = new Date(today);
      from.setDate(from.getDate() - (daysBack[period] ?? 0));
      this.dateFrom = fmt(from);
      this.dateTo = fmt(today);
    }
    this.pagination.page = 1;
    this.loadEnhancedStats();
    this.loadProducts();
  }

  // Al editar las fechas a mano, se deselecciona el botón de período (queda rango "custom").
  onManualDateChange() {
    this.selectedPeriod = '';
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
    if (tab === 'overview') {
      setTimeout(() => this.buildCharts(), 100);
    }
  }

  // === CHARTS ===

  destroyCharts() {
    this.timeSeriesChart?.destroy();
    this.topProductsChart?.destroy();
    this.sectorChart?.destroy();
    this.timeSeriesChart = null;
    this.topProductsChart = null;
    this.sectorChart = null;
  }

  buildCharts() {
    this.destroyCharts();
    if (!this.enhanced) return;
    this.buildTimeSeriesChart();
    this.buildTopProductsChart();
    this.buildSectorChart();
  }

  buildTimeSeriesChart() {
    const canvas = document.getElementById('timeSeriesChart') as HTMLCanvasElement;
    if (!canvas || !this.enhanced?.time_series?.length) return;

    const data = this.enhanced.time_series;
    this.timeSeriesChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: data.map((d: any) => {
          const parts = d.date.split('-');
          return `${parts[2]}/${parts[1]}`;
        }),
        datasets: [
          {
            label: 'Unidades',
            data: data.map((d: any) => d.units),
            borderColor: '#8b5cf6',
            backgroundColor: 'rgba(139, 92, 246, 0.1)',
            fill: true,
            tension: 0.4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { position: 'top' } },
        scales: {
          y: { type: 'linear', display: true, position: 'left', title: { display: true, text: 'Unidades' }, beginAtZero: true }
        }
      }
    });
  }

  buildTopProductsChart() {
    const canvas = document.getElementById('topProductsChart') as HTMLCanvasElement;
    if (!canvas || !this.enhanced?.top_purchased?.length) return;

    const data = this.enhanced.top_purchased;
    this.topProductsChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: data.map((d: any) => d.name?.substring(0, 25) || d.code),
        datasets: [{
          label: 'Unidades vendidas',
          data: data.map((d: any) => d.total_units),
          backgroundColor: [
            '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981',
            '#6366f1', '#14b8a6', '#f97316', '#ef4444', '#84cc16'
          ],
          borderRadius: 6
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: { x: { grid: { display: false } } }
      }
    });
  }


  buildSectorChart() {
    const canvas = document.getElementById('sectorChart') as HTMLCanvasElement;
    if (!canvas || !this.enhanced?.by_sector?.length) return;

    const data = this.enhanced.by_sector;
    this.sectorChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: data.map((d: any) => this.getSectorName(d.se)),
        datasets: [
          {
            label: 'Unidades',
            data: data.map((d: any) => d.total_units),
            backgroundColor: '#10b981',
            borderRadius: 6
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          y: { type: 'linear', display: true, position: 'left', title: { display: true, text: 'Unidades' }, beginAtZero: true }
        }
      }
    });
  }


  onSeChange(se: number | null) {
    this.selectedSectors = (se === null || se === undefined) ? [] : [se];
    this.pagination.page = 1;
    this.loadProducts();
  }

  // === TABLE ===

  onSearch(value: string) {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.searchTerm = value;
      this.pagination.page = 1;
      this.loadProducts();
    }, 400);
  }

  onSort(field: string) {
    if (this.sortField === field) {
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    } else {
      this.sortField = field;
      this.sortOrder = 'desc';
    }
    this.pagination.page = 1;
    this.loadProducts();
  }

  goToPage(page: number) {
    if (page < 1 || page > this.pagination.pages) return;
    this.pagination.page = page;
    this.loadProducts();
  }

  viewProduct(product: any) {
    this.router.navigate(['/console/catalog-products/detail', product.code]);
  }

  // === CREAR PRODUCTO ===

  openCreateModal() {
    this.newProduct = { code: '', name: '', brand: '', category: '', ean_upc: '', material: '', numero_material: '', se: null };
    this.showCreateModal = true;
  }

  closeCreateModal() {
    this.showCreateModal = false;
  }

  async saveNewProduct() {
    if (!this.newProduct.code?.trim() || !this.newProduct.name?.trim()) {
      alertify.error('Código y nombre son obligatorios');
      return;
    }

    this.savingProduct = true;
    try {
      await this.catalogService.createProduct({
        code: this.newProduct.code.trim(),
        name: this.newProduct.name.trim(),
        brand: this.newProduct.brand?.trim() || undefined,
        category: this.newProduct.category?.trim() || undefined,
        ean_upc: this.newProduct.ean_upc?.trim() || undefined,
        material: this.newProduct.material?.trim() || undefined,
        numero_material: this.newProduct.numero_material?.trim() || undefined,
        se: this.newProduct.se || undefined
      });
      alertify.success('Producto creado exitosamente');
      this.closeCreateModal();
      this.loadData();
    } catch (error: any) {
      const msg = error?.error?.msg || 'Error al crear producto';
      alertify.error(msg);
    } finally {
      this.savingProduct = false;
    }
  }

  // === EDITAR PRODUCTO ===

  startEdit(product: any, event: Event) {
    event.stopPropagation();
    this.editingProduct = product;
    this.editForm = {
      name: product.name || '',
      brand: product.brand || '',
      category: product.category || '',
      material: product.material || '',
      numero_material: product.numero_material || '',
      se: product.se || null
    };
  }

  cancelEdit() {
    this.editingProduct = null;
  }

  async saveEdit() {
    if (!this.editingProduct) return;
    this.savingEdit = true;
    try {
      await this.catalogService.updateProduct(this.editingProduct.code, this.editForm);
      alertify.success('Producto actualizado');
      this.editingProduct = null;
      this.loadProducts();
    } catch (error: any) {
      alertify.error(error?.error?.msg || 'Error al actualizar');
    } finally {
      this.savingEdit = false;
    }
  }

  // === ELIMINAR PRODUCTO ===

  async deleteProduct(product: any, event: Event) {
    event.stopPropagation();
    if (!confirm(`¿Desactivar el producto "${product.name}"?`)) return;

    try {
      await this.catalogService.deleteProduct(product.code);
      alertify.success('Producto desactivado');
      this.loadData();
    } catch (error: any) {
      alertify.error(error?.error?.msg || 'Error al desactivar');
    }
  }

  // === EXPORTAR XLS ===

  async exportToXLS() {
    this.exporting = true;
    try {
      const f = this.currentFilters();
      const resp = await this.catalogService.getList({
        page: 1,
        limit: 10000,
        search: this.searchTerm || undefined,
        se: undefined,
        sort: this.sortField,
        order: this.sortOrder,
        period: f.period,
        dateFrom: f.dateFrom,
        dateTo: f.dateTo,
        shop: f.shop,
        sector: f.sector,
        plan: f.plan
      });
      const allProducts = resp.res || [];

      if (allProducts.length === 0) {
        alertify.warning('No hay productos para exportar');
        return;
      }

      const tiendasFmt = (codes: any[]) => (codes || []).map((c: any) => {
        const n = this.getShopName(c);
        return n ? `${c} - ${n}` : `${c}`;
      }).join(', ');

      const rows = allProducts.map((p: any) => ({
        'Código': p.code || '',
        'Nombre': p.name || '',
        'Material': p.material || '',
        'N° Material': p.numero_material || '',
        'Marca': p.brand || '',
        'Categoría': p.category || '',
        'Sector (SE)': p.se || '',
        'Sector (Nombre)': p.se ? this.getSectorName(p.se) : '',
        'EAN / UPC': p.ean_upc || '',
        'Compras': p.stats?.total_purchases || 0,
        'Unidades Vendidas': p.stats?.total_quantity_sold || 0,
        'Tiendas (cód - agencia)': tiendasFmt(p.stats?.shops),
        'Estado': p.status ? 'Activo' : 'Inactivo'
      }));

      const ws = XLSX.utils.json_to_sheet(rows);

      ws['!cols'] = [
        { wch: 22 }, // Código
        { wch: 40 }, // Nombre
        { wch: 22 }, // Material
        { wch: 40 }, // N° Material
        { wch: 15 }, // Marca
        { wch: 18 }, // Categoría
        { wch: 10 }, // Sector SE
        { wch: 22 }, // Sector Nombre
        { wch: 16 }, // EAN
        { wch: 10 }, // Compras
        { wch: 16 }, // Unidades
        { wch: 40 }, // Tiendas
        { wch: 10 }, // Estado
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Productos de Compra');

      // Hoja 2: filtros aplicados (transparencia)
      const filtros = [
        { Filtro: 'Desde', Valor: this.dateFrom || '—' },
        { Filtro: 'Hasta', Valor: this.dateTo || '—' },
        { Filtro: 'Período', Valor: !this.dateFrom && !this.dateTo ? (this.selectedPeriod || '—') : '—' },
        { Filtro: 'Tienda', Valor: this.selectedShops.length ? this.selectedShops.map(c => this.shopLabel(c)).join('; ') : 'Todas' },
        { Filtro: 'Sector', Valor: this.selectedSectors.length ? this.selectedSectors.map(s => this.getSectorName(s)).join('; ') : 'Todos' },
        { Filtro: 'Producto (búsqueda)', Valor: this.searchTerm || '—' },
        { Filtro: 'Producto (código exacto)', Valor: this.productCode || '—' },
        { Filtro: 'Plan de compra', Valor: this.selectedPlans.length ? this.selectedPlans.map(p => this.planLabel(p)).join('; ') : 'Todos' },
        { Filtro: 'Borradores', Valor: 'Excluidos' },
        { Filtro: 'Generado', Valor: new Date().toLocaleString('es-VE') },
        { Filtro: 'Total registros', Valor: allProducts.length }
      ];
      const wsFiltros = XLSX.utils.json_to_sheet(filtros);
      wsFiltros['!cols'] = [{ wch: 28 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, wsFiltros, 'Filtros');

      // Hoja 3: ventas por tienda (TODAS las tiendas, con montos USD/Bs)
      if (this.allShopRows.length) {
        const shopRows = this.allShopRows.map((s: any) => ({
          'Código Tienda': s.shop,
          'Agencia': s.shop_name || this.getShopName(s.shop) || '',
          'Compras': s.purchases || 0,
          'Unidades': s.total_units || 0,
          'Vendido USD': s.revenue_usd || 0,
          'Vendido Bs': s.revenue_bs || 0,
          'Productos únicos': s.unique_products || 0
        }));
        const wsShop = XLSX.utils.json_to_sheet(shopRows);
        wsShop['!cols'] = [{ wch: 14 }, { wch: 28 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 18 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, wsShop, 'Por Tienda');
      }

      const today = new Date().toISOString().split('T')[0];
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], { type: 'application/octet-stream' });
      saveAs(blob, `productos_compra_${today}.xlsx`);

      alertify.success(`${allProducts.length} productos exportados`);
    } catch (error) {
      console.error('Error exportando productos:', error);
      alertify.error('Error al exportar productos');
    } finally {
      this.exporting = false;
    }
  }

  // Export rápido del desglose por tienda (TODAS las tiendas, con montos USD/Bs)
  exportShopsToXLS() {
    try {
      const rows = this.allShopRows.map((s: any) => ({
        'Código Tienda': s.shop,
        'Agencia': s.shop_name || this.getShopName(s.shop) || '',
        'Compras': s.purchases || 0,
        'Unidades': s.total_units || 0,
        'Vendido USD': s.revenue_usd || 0,
        'Vendido Bs': s.revenue_bs || 0,
        'Productos únicos': s.unique_products || 0
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 14 }, { wch: 28 }, { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 16 }, { wch: 16 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Por Tienda');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `ventas_por_tienda_${new Date().toISOString().split('T')[0]}.xlsx`);
      alertify.success(`${rows.length} tiendas exportadas`);
    } catch (error) {
      console.error('Error exportando tiendas:', error);
      alertify.error('Error al exportar tiendas');
    }
  }

  // === HELPERS ===


  formatCurrency(value: number): string {
    if (!value && value !== 0) return '-';
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  formatNumber(value: number): string {
    if (!value && value !== 0) return '0';
    return value.toLocaleString('es-VE');
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) return 'fas fa-sort';
    return this.sortOrder === 'desc' ? 'fas fa-sort-down' : 'fas fa-sort-up';
  }
}
